import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Typography,
  Statistic,
  message,
  Badge,
  Divider,
} from 'antd';
import {
  ClockCircleOutlined,
  PlayCircleOutlined,
  DatabaseOutlined,
  InfoCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import {
  apiFormsGetTemplateSchedulerStatus,
  apiFormsProcessNextTemplate,
} from '../services/api/forms';
// We'll need to determine the API_URL. For now, let's use a temporary approach
// const { API_URL } = require('../../services/api/constants');

const { Title, Text } = Typography;

interface TemplateSchedulerProps {
  token: string;
  onTemplateProcessed?: () => void;
}

interface SchedulerStatus {
  pendingTemplates: number;
  nextScheduledRun: string;
  status: string;
}

const TemplateScheduler: React.FC<TemplateSchedulerProps> = ({
  token,
  onTemplateProcessed,
}) => {
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState<SchedulerStatus | null>(null);
  const prevStatusRef = React.useRef<string | null>(null);

  const fetchStatus = async () => {
    if (!token) return;

    try {
      setRefreshing(true);
      const [statusResponse, statusCode] =
        await apiFormsGetTemplateSchedulerStatus(token);

      if (statusCode === 200) {
        // Check if status changed from "running" to something else
        const prevStatus = prevStatusRef.current;
        const newStatus = statusResponse.status?.toLowerCase();

        if (prevStatus === 'running' && newStatus !== 'running') {
          // Status changed from running to something else - processing finished
          message.success(
            '✅ Template processing completed! Refreshing template list...',
          );
          onTemplateProcessed?.();
        }

        // Update the previous status reference
        prevStatusRef.current = newStatus;

        setStatus(statusResponse);
      } else {
        message.error('Failed to fetch scheduler status');
      }
    } catch (error) {
      console.error('Error fetching scheduler status:', error);
      message.error('Failed to fetch scheduler status');
    } finally {
      setRefreshing(false);
    }
  };

  const handleProcessNext = async () => {
    if (!token) return;

    try {
      setLoading(true);
      const [response, statusCode] = await apiFormsProcessNextTemplate(token);

      if (statusCode === 200 || statusCode === 201) {
        message.success('✅ Template processing initiated successfully!');
        await fetchStatus(); // Refresh the status
        onTemplateProcessed?.();
      } else {
        message.error('Failed to process template');
      }
    } catch (error) {
      console.error('Error processing template:', error);
      message.error('Failed to process template');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [token]);

  // Auto-refresh when status is running
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (status?.status?.toLowerCase() === 'running') {
      // Refresh every 10 seconds when running
      interval = setInterval(() => {
        fetchStatus();
      }, 10000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [status?.status]);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'running':
        return 'processing';
      case 'inactive':
        return 'default';
      case 'error':
        return 'error';
      default:
        return 'processing';
    }
  };

  return (
    <Card
      title={
        <Space>
          <DatabaseOutlined />
          <span>Google Sheets Template Scheduler</span>
          <Badge
            status={status ? getStatusColor(status.status) : 'default'}
            text={status?.status || 'Loading...'}
          />
        </Space>
      }
      extra={
        <Button
          type="text"
          icon={<SyncOutlined spin={refreshing} />}
          onClick={fetchStatus}
          disabled={refreshing}
          size="small"
        >
          Refresh
        </Button>
      }
      style={{
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        border: '1px solid #e1e8f0',
        marginBottom: 24,
      }}
    >
      <div style={{ marginBottom: 16 }}>
        {status?.status?.toLowerCase() === 'running' && (
          <div
            style={{
              padding: 12,
              background: 'rgba(24, 144, 255, 0.1)',
              border: '1px solid #91d5ff',
              borderRadius: 6,
              marginBottom: 12,
            }}
          >
            <Text
              style={{ color: '#1890ff', fontSize: '14px', fontWeight: '500' }}
            >
              🔄 Processing in progress... The scheduler is currently creating a
              template from your spreadsheet.
            </Text>
          </div>
        )}

        <Text type="secondary" style={{ fontSize: '14px' }}>
          <InfoCircleOutlined style={{ marginRight: 8 }} />
          Automatically processes templates from your Google Spreadsheet daily
          at 3:00 PM UTC.{' '}
          <a
            href="https://docs.google.com/spreadsheets/d/1ij3Wz5icOEOEuv9ctyJSFfEGniSzehZ64d93an1XiL4/edit?gid=0#gid=0"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'underline' }}
          >
            Open your template spreadsheet
          </a>{' '}
          with columns: Name, Slug, Business Category, Description, Brand Color
          (hex), Additional AI prompt, Created (YES/NO)
        </Text>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Space direction="vertical" size="large" style={{ flex: 1 }}>
          <div style={{ display: 'flex', gap: 24 }}>
            <Statistic
              title="Pending Templates"
              value={status?.pendingTemplates ?? 0}
              prefix={<DatabaseOutlined />}
              loading={refreshing}
            />
            <Statistic
              title="Next Scheduled Run"
              value={status?.nextScheduledRun || 'Loading...'}
              prefix={<ClockCircleOutlined />}
              loading={refreshing}
            />
          </div>

          <Divider style={{ margin: '16px 0' }} />

          <div>
            <Title level={5} style={{ margin: 0, marginBottom: 8 }}>
              Manual Processing
            </Title>
            <Text
              type="secondary"
              style={{ fontSize: '13px', display: 'block', marginBottom: 12 }}
            >
              Process the next template immediately without waiting for the
              scheduled run
            </Text>
            <Space>
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={handleProcessNext}
                loading={loading}
                disabled={
                  refreshing ||
                  status?.pendingTemplates === 0 ||
                  status?.status?.toLowerCase() === 'running'
                }
                style={{
                  background:
                    status?.pendingTemplates === 0 ||
                    status?.status?.toLowerCase() === 'running'
                      ? undefined
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                }}
              >
                {status?.status?.toLowerCase() === 'running'
                  ? 'Processing Template...'
                  : status?.pendingTemplates === 0
                  ? 'No Templates to Process'
                  : 'Process Next Template'}
              </Button>

              {status?.status?.toLowerCase() === 'running' ? (
                <Text
                  type="secondary"
                  style={{ fontSize: '12px', color: '#1890ff' }}
                >
                  🔄 Currently processing a template from your spreadsheet...
                </Text>
              ) : status?.pendingTemplates && status.pendingTemplates > 0 ? (
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Next: Process &quot;{status.pendingTemplates}&quot; pending
                  template
                  {status.pendingTemplates !== 1 ? 's' : ''}
                </Text>
              ) : null}
            </Space>
          </div>
        </Space>
      </div>

      {status?.pendingTemplates === 0 && (
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              padding: 12,
              background: 'rgba(82, 196, 26, 0.1)',
              border: '1px solid #b7eb8f',
              borderRadius: 6,
              marginBottom: 12,
            }}
          >
            <Text style={{ color: '#389e0d', fontSize: '14px' }}>
              ✅ All templates have been processed! Add more templates to your
              Google Spreadsheet to continue.
            </Text>
          </div>

          <div
            style={{
              padding: 16,
              background: 'rgba(24, 144, 255, 0.05)',
              border: '1px solid #91d5ff',
              borderRadius: 6,
            }}
          >
            <Title
              level={5}
              style={{ margin: 0, marginBottom: 8, color: '#1890ff' }}
            >
              📝 How to add templates to your spreadsheet:
            </Title>
            <Text
              style={{ fontSize: '13px', display: 'block', lineHeight: '1.6' }}
            >
              1.{' '}
              <a
                href="https://docs.google.com/spreadsheets/d/1ij3Wz5icOEOEuv9ctyJSFfEGniSzehZ64d93an1XiL4/edit?gid=0#gid=0"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'underline', color: '#1890ff' }}
              >
                Open your template spreadsheet
              </a>
              <br />
              2. Add a new row with: Name, Slug, Category, Description, Color
              (#hex), AI Prompt, and leave &quot;Created&quot; empty
              <br />
              3. The scheduler will automatically pick it up at the next run
              time (2 templates will be created)
            </Text>
          </div>
        </div>
      )}
    </Card>
  );
};

export default TemplateScheduler;
